/**
 * Shared PV1–PV4 payout policy helpers (venues.payout_policy).
 * Used by Payout Rules Builder, Venue payouts admin, and Payout Capture.
 *
 * `getSignerSlots` / defaults: keep in sync with `mobile/payoutPolicyUtils.js` and
 * `backend/src/utils/payoutSignerSlots.js`.
 * The TIERS list and PV1-PV4 defaults are drift-checked by
 * `backend/tests/modules/16-payout-policy-sync.guard.test.js`.
 *
 * Signer model (replaces legacy signerRoleIds / signaturesRequired):
 *   mandatorySigners       — roles that MUST always sign (each gets a fixed slot)
 *   optionalSigners        — pool of roles that may sign (staff chooses from this list)
 *   optionalSignaturesRequired — how many from optionalSigners must sign
 *
 * Role identifiers are `staff_roles.role_key` values; `patron` is a reserved
 * sentinel for the player. Display labels come from the staff_roles lookup,
 * not from this file.
 */

export const TIERS = ['PV1', 'PV2', 'PV3', 'PV4']

export const defaultPolicyForTier = (tierId) => {
  const base = (enabled, kyc, occ, minA, maxA, cash, pin, mandatory, optional, optReq, issuer) => ({
    enabled,
    kycRequired: kyc,
    occupationRequired: occ,
    minAmount: minA,
    maxAmount: maxA,
    cashDispensed: cash,
    pinEntryByRoleId: pin,
    mandatorySigners: mandatory,
    optionalSigners: optional,
    optionalSignaturesRequired: optReq,
    issuerRoleId: issuer,
  })
  switch (tierId) {
    case 'PV1':
      // No signatures needed for small payouts
      return base(true, false, false, 0, 2000, 'CRT_OR_CASH', null, [], [], 0, 'cashier')
    case 'PV2':
      // Patron acknowledgement only
      return base(true, false, false, 2000, 5000, 'CASH_ONLY', null, ['patron'], [], 0, 'duty_manager')
    case 'PV3':
      // Patron must sign + 1 staff from optional pool
      return base(true, true, false, 5000, 10000, 'NO_CASH', 'duty_manager', ['patron'], ['duty_manager', 'gaming_manager'], 1, 'gaming_manager')
    case 'PV4':
    default:
      // Patron must sign + 2 staff from optional pool
      return base(true, true, false, 10000, null, 'NO_CASH', 'duty_manager', ['patron'], ['duty_manager', 'gaming_manager', 'venue_manager'], 2, 'gaming_manager')
  }
}

/**
 * Mandatory signers take precedence; optional pool must not include the same role.
 */
export function normalizeSignerLists(mandatorySigners, optionalSigners, optionalSignaturesRequired = 0) {
  const mandatory = Array.isArray(mandatorySigners) ? [...new Set(mandatorySigners)] : []
  const mandatorySet = new Set(mandatory)
  const optional = (Array.isArray(optionalSigners) ? optionalSigners : []).filter((id) => !mandatorySet.has(id))
  const uniqueOptional = [...new Set(optional)]
  const req = Math.max(0, Math.min(Number(optionalSignaturesRequired) || 0, uniqueOptional.length))
  return {
    mandatorySigners: mandatory,
    optionalSigners: uniqueOptional,
    optionalSignaturesRequired: req,
  }
}

export const ensureVenuePolicy = (venue) => {
  const policy = { ...(venue.payoutPolicy || {}) }
  TIERS.forEach((t) => {
    const def = defaultPolicyForTier(t)
    const existing = policy[t] || {}
    const merged = { ...def }
    for (const [k, v] of Object.entries(existing)) {
      if (v !== undefined) merged[k] = v
    }
    // Strip legacy fields
    delete merged.signerRoleIds
    delete merged.signaturesRequired
    delete merged.authorizationChain
    const signers = normalizeSignerLists(
      merged.mandatorySigners,
      merged.optionalSigners,
      merged.optionalSignaturesRequired,
    )
    merged.mandatorySigners = signers.mandatorySigners
    merged.optionalSigners = signers.optionalSigners
    merged.optionalSignaturesRequired = signers.optionalSignaturesRequired
    policy[t] = merged
  })
  return { ...venue, payoutPolicy: policy }
}

/** API venue row → merged payoutPolicy object */
export function payoutPolicyFromVenueRow(v) {
  let policy = {}
  if (v.payout_policy) {
    try {
      policy = typeof v.payout_policy === 'object' ? v.payout_policy : JSON.parse(v.payout_policy)
    } catch (_) {
      policy = {}
    }
  }
  return ensureVenuePolicy({ payoutPolicy: policy }).payoutPolicy
}

/**
 * Pick PV tier from amount using venue policy (first matching bracket PV1→PV4).
 * @param {number} amount
 * @param {Record<string, object>} fullPolicy — output of payoutPolicyFromVenueRow
 */
export function resolveTierFromAmount(amount, fullPolicy) {
  const n = Number(amount)
  if (Number.isNaN(n) || n < 0) return 'PV1'
  for (const tid of TIERS) {
    const p = fullPolicy[tid]
    if (!p?.enabled) continue
    const min = Number(p.minAmount ?? 0)
    const max = p.maxAmount == null || p.maxAmount === '' ? null : Number(p.maxAmount)
    if (n < min) continue
    if (max != null && n > max) continue
    return tid
  }
  return 'PV1'
}

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '∞'
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount)
}

export const formatTierRange = (min, max) => {
  if (max === null || max === undefined || max === '') return `${formatCurrency(min)}+`
  return `${formatCurrency(min)} – ${formatCurrency(max)}`
}

/** Patron sentinel role id + label (the player is not a staff_roles entry). */
export const PATRON_ROLE_ID = 'patron'
export const PATRON_LABEL = 'Player'

/**
 * Build signature capture slots from the new mandatory/optional signer model.
 *
 * Mandatory slots: one per mandatorySigners entry — fixed, cannot be changed by staff.
 * Optional slots:  optionalSignaturesRequired slots — staff picks the signer from
 *                  the optionalSigners pool at capture time.
 *
 * Each slot:
 *   { key, roleId, label, isMandatory, optionRoleIds }
 *   - isMandatory: true → fixed role, no picker shown
 *   - isMandatory: false → roleId is null at init; optionRoleIds lists the pool
 *
 * @param {object} policy — single PV tier object
 * @returns {{ key: string, roleId: string|null, label: string, isMandatory: boolean, optionRoleIds: string[] }[]}
 */
export function getSignerSlots(policy) {
  if (!policy) return []
  const mandatory = Array.isArray(policy.mandatorySigners) ? policy.mandatorySigners : []
  const optional = Array.isArray(policy.optionalSigners) ? policy.optionalSigners : []
  const optReq = Math.max(0, Number(policy.optionalSignaturesRequired ?? 0))

  const slots = []

  for (let i = 0; i < mandatory.length; i++) {
    const roleId = mandatory[i]
    slots.push({
      key: `mandatory-${i}-${roleId}`,
      roleId,
      label: roleId,
      isMandatory: true,
      optionRoleIds: [],
    })
  }

  for (let i = 0; i < optReq; i++) {
    slots.push({
      key: `optional-${i}`,
      roleId: null,
      label: 'Select signer',
      isMandatory: false,
      optionRoleIds: [...optional],
    })
  }

  return slots
}

/** Total number of signatures the capture form will collect for a tier. */
export function totalSignaturesForPolicy(policy) {
  if (!policy) return 0
  const mandatory = Array.isArray(policy.mandatorySigners) ? policy.mandatorySigners.length : 0
  const optReq = Math.max(0, Number(policy.optionalSignaturesRequired ?? 0))
  return mandatory + optReq
}
